/* tanstack table */
import { Table } from '@tanstack/react-table';

/* Shared */
import { View } from 'application/Shared/Components/Table/types';
import { Helpers } from 'application/Shared/Helpers';

/*  NPM  */
import equal from 'fast-deep-equal';

/* React */
import { useEffect } from 'react';

/**
 * A custom hook that signals if view has been modified.
 *
 * @template TData The type of data in the table.
 * @param {View} props.currentTableView - object containing columnVisibility, columnOrder, columnFilters, and/or sorting.
 * @param {boolean} props.ignoreSearchParams - A flag to ignore URL parameters.
 * @param {function} props.setViewHasBeenModified - function to set if view has been modified.
 * @param {Table<TData>} props.table - The table instance from @tanstack/react-table.
 */
export const useHasViewBeenModified = <TData>({
    currentTableView,
    ignoreSearchParams,
    setViewHasBeenModified,
    table
}: {
    currentTableView: View;
    ignoreSearchParams?: boolean;
    setViewHasBeenModified: (value: boolean) => void;
    table: Table<TData>;
}): void => {
    const { columnFilters, columnOrder, columnVisibility, sorting } =
        table.getState();

    const state = currentTableView?.state;

    useEffect(() => {
        if (ignoreSearchParams || !state) {
            return;
        }

        let viewHasBeenModified = !Helpers.areEqual(
            columnFilters,
            state.columnFilters
        );

        if (!viewHasBeenModified) {
            viewHasBeenModified = !equal(columnOrder, state.columnOrder);
        }

        if (!viewHasBeenModified) {
            viewHasBeenModified = !Helpers.areEqual(
                columnVisibility,
                state.columnVisibility
            );
        }

        if (!viewHasBeenModified) {
            viewHasBeenModified = !Helpers.areEqual(sorting, state.sorting);
        }

        setViewHasBeenModified(viewHasBeenModified);
    }, [
        columnFilters,
        columnOrder,
        columnVisibility,
        ignoreSearchParams,
        setViewHasBeenModified,
        sorting,
        state
    ]);
};
